import { ChangeEvent, useState } from 'react';
import { Icon as BlueIcon } from '@blueprintjs/core';

import ReactDom from 'react-dom';

import * as yup from 'yup';
import BackIconImage from '@assets/svg/back-icon.svg';
import AvatarDefaultImg from '@assets/svg/avatar-placeholder.svg';

import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { useMutation, useQueryClient } from 'react-query';
import { patchUsers } from '@services/queries/users/patchUsers/patchUsers';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { extractUrl } from '@utils/updateBeamUrl/updateBeamUrl';
import {
  Container,
  Overlay,
  Close,
  Header,
  Title,
  Back,
  Confirm,
  ProfileImg,
  Row,
  Column,
  Label,
  Line,
  FileName,
  IconCloseAwesome,
  Input,
  SelectFile,
  Form,
  Divider,
  SpacingDivider,
} from './EditUserDetails.styles';

interface IEditUserDetails {
  open: boolean;
  onClose: () => void;
  userName: string;
  userPicture: string;
  userId: string;
}

interface IFormData {
  name: string;
  picture: string;
  file?: FileList;
}

export const EditUserDetails = ({
  open,
  onClose,
  userName,
  userPicture,
  userId,
}: IEditUserDetails) => {
  const portalDiv = document.getElementById('portal');
  const queryClient = useQueryClient();

  const [edited, setEdited] = useState<boolean>(false);
  const [topic, setTopic] = useState<'user' | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [previewFilePicture, setPreviewFilePicture] = useState<string>('');

  const schema = yup
    .object({
      name: yup.string(),
      picture: yup.string(),
      file: yup.mixed(),
    })
    .required();

  const mutationEditUser = useMutation(patchUsers, {
    onSuccess: () => {
      onClose();
      queryClient.invalidateQueries(['membersAuth0']);
      queryClient.invalidateQueries(['profile']);
    },
  });

  const {
    register,
    handleSubmit,
    formState: { isValid },
    setValue,
  } = useForm<IFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: userName || '',
      picture: userPicture || '',
    },
    mode: 'onChange',
  });

  const onSubmit = (data: IFormData) => {
    const result: any = {
      id: userId,
      name: data.name,
      picture: extractUrl(data.picture),
      file: data.file?.[0],
    };
    if (!mutationEditUser.isLoading) {
      mutationEditUser.mutate(result);
    }
  };

  const previewFileImage = (e: ChangeEvent<HTMLInputElement>) => {
    const imagePreviewFileUrl = e.target.files;
    if (imagePreviewFileUrl && imagePreviewFileUrl.length > 0) {
      const imageSrc = URL.createObjectURL(imagePreviewFileUrl[0]);
      setPreviewFilePicture(imageSrc);
    }
  };

  const ProfileImage = () => {
    if (previewFilePicture.length) return previewFilePicture;
    if (userPicture) return userPicture;
    return AvatarDefaultImg;
  };

  const Logo = () => {
    if (fileName !== '')
      return (
        <FileName>
          {fileName}
          <IconCloseAwesome
            icon={faTimes}
            onClick={() => {
              setFileName('');
              setValue('file', undefined);
              setPreviewFilePicture('');
            }}
          />
        </FileName>
      );
    return (
      <div>
        <SelectFile htmlFor="upload">Select File</SelectFile>
        <input
          type="file"
          id="upload"
          hidden
          accept="image/*"
          {...register('file')}
          onChange={e => {
            register('file').onChange(e);
            setFileName(e.target.files?.[0]?.name || '');
            setEdited(true);
            previewFileImage(e);
          }}
        />
      </div>
    );
  };

  return open && portalDiv
    ? ReactDom.createPortal(
        <Overlay>
          <Container>
            <Header>
              {topic && (
                <>
                  <Back
                    src={BackIconImage}
                    alt="back"
                    onClick={() => setTopic(null)}
                  />
                  <SpacingDivider />
                </>
              )}
              <Title>Edit Profile</Title>
              <Close
                onClick={() => {
                  onClose();
                  setPreviewFilePicture('');
                }}
              >
                <BlueIcon icon="cross" color="#8F95B2" />
              </Close>
            </Header>
            <Divider />
            <Form onSubmit={handleSubmit(onSubmit)}>
              <Row>
                <ProfileImg src={ProfileImage()} alt="Profile Img" />

                <Column>
                  <Label className="picture">Picture</Label>
                  {Logo()}
                </Column>
              </Row>

              <Label>Name</Label>

              <Input
                type="text"
                placeholder="John Doe"
                {...register('name')}
                onChange={() => {
                  setEdited(true);
                }}
                className="infoInputs"
              />

              <Line>
                <Confirm className="cancel" onClick={() => onClose()}>
                  Cancel
                </Confirm>
                <Confirm
                  disabled={!isValid || !edited || mutationEditUser.isLoading}
                  type="submit"
                >
                  Save
                </Confirm>
              </Line>
            </Form>
          </Container>
        </Overlay>,
        portalDiv,
      )
    : null;
};
